import { TRADING_NAME } from "@/constants/company";

export type FaqItem = {
  question: string;
  answer: string;
};

export type FaqCategory = {
  category: string;
  items: FaqItem[];
};

/** Phoenix-approved public finance answer. Shared by /faq and the homepage FAQ. */
export const FINANCE_FAQ: FaqItem = {
  question: "Do you offer finance?",
  answer:
    "We are an Introducer Appointed Representative of Phoenix Financial Consultants Limited, a credit broker, not a lender. Finance options are available through Phoenix, subject to status.",
};

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    category: "Solar basics",
    items: [
      {
        question: "Is my home suitable for solar panels?",
        answer:
          "Most UK homes with a south, south-east, or south-west facing roof are suitable for solar. East and west-facing roofs can still generate good returns. Our AI assessment analyses your roof orientation, pitch, shading, and local irradiance data to give you a personalised suitability score. Flat roofs can also accommodate solar with angled mounting frames.",
      },
      {
        question: "How much could I save with solar?",
        answer:
          "The average UK homeowner saves £600–£1,400 per year with a typical 4kWp system, depending on their energy consumption, tariff, and how much of the solar generation they use directly. With a battery, self-consumption typically rises from 30–40% to 70–80%, significantly improving savings. Our AI calculates your personalised estimate based on your actual energy bill.",
      },
      {
        question: "How long does a solar system last?",
        answer:
          `Solar panels typically come with a 25-year performance warranty and can last 30+ years. Inverters generally need replacing after 10–15 years. Batteries typically carry a 10-year warranty. All systems installed through ${TRADING_NAME} use Tier 1 components with manufacturer warranties.`,
      },
      {
        question: "Do I need planning permission for solar panels?",
        answer:
          "In most cases, no. Solar panels on residential properties in England and Wales are permitted development, meaning no planning permission is required. Exceptions include listed buildings, properties in conservation areas, and some flats. Your installer will confirm this during the survey.",
      },
    ],
  },
  {
    category: `The ${TRADING_NAME} process`,
    items: [
      {
        question: `How does the ${TRADING_NAME} quote process work?`,
        answer:
          "It's simple: upload your energy bill (or enter your postcode for an estimate), our AI analyses your property and energy usage, and you receive an instant solar proposal with system options and savings projections. If you'd like to proceed, you pay a small deposit to confirm your project and we match you with a local MCS-certified installer who will conduct a full survey.",
      },
      {
        question: "How long does it take from quote to installation?",
        answer:
          "Typically 4–8 weeks from deposit to installation, depending on installer availability and any grid connection requirements. The survey usually happens within 1–2 weeks of deposit. Installation itself takes 1–2 days for a standard residential system.",
      },
      {
        question: "Who installs my solar system?",
        answer:
          "All installations are carried out by independent MCS-certified solar installers in our vetted network. MCS (Microgeneration Certification Scheme) certification is the UK industry standard and is required to access government incentives like the Smart Export Guarantee. We only work with installers who meet our quality and compliance standards.",
      },
      {
        question: "What is the deposit for?",
        answer:
          "The deposit confirms your project and secures your slot with an installer. It is fully refundable if the installer's survey concludes your property is unsuitable for solar, or if we cannot match you with an installer within 14 days. The deposit is deducted from the total installation cost.",
      },
    ],
  },
  {
    category: "Finance & incentives",
    items: [
      FINANCE_FAQ,
      {
        question: "What is the Smart Export Guarantee (SEG)?",
        answer:
          "The Smart Export Guarantee is a UK government scheme that requires energy suppliers to pay you for surplus solar electricity you export to the grid. Rates vary by supplier but typically range from 3p to 24p per kWh. You must have an MCS-certified installation to be eligible. We help you identify the best SEG tariff for your situation.",
      },
      {
        question: "Is there VAT on solar panels?",
        answer:
          "Yes — solar panel installations in the UK currently attract 0% VAT (reduced from 20% in April 2022 as part of the government's energy cost support measures). This applies to the full installation including panels, inverter, and battery storage.",
      },
      {
        question: "What is the payback period for solar?",
        answer:
          "The average payback period for a UK solar installation is 7–12 years, depending on system size and energy usage. With a battery, payback is typically 9–14 years. After payback, the system generates free electricity for the remainder of its 25+ year lifespan.",
      },
    ],
  },
  {
    category: "After installation",
    items: [
      {
        question: "What warranties do I get?",
        answer:
          `All systems installed through ${TRADING_NAME} include manufacturer warranties on panels (typically 25 years performance, 10–12 years product), inverter (5–12 years), and battery (10 years). Your installer also provides a workmanship warranty. Full warranty details are included in your installation documents.`,
      },
      {
        question: "Do I need to maintain my solar panels?",
        answer:
          `Solar panels require minimal maintenance. Rain generally keeps them clean, but an annual clean and inspection is recommended to maintain optimal performance. Your installer will advise on maintenance during handover. ${TRADING_NAME}'s platform includes aftercare reminders and a direct channel to your installer.`,
      },
      {
        question: "What happens if something goes wrong?",
        answer:
          `You can contact your installer directly through the ${TRADING_NAME} platform. If you have a complaint that cannot be resolved with the installer, you can raise it with ${TRADING_NAME} and, if necessary, with RECC (Renewable Energy Consumer Code) who provide independent dispute resolution.`,
      },
    ],
  },
];
